import { useState, useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// Storage key for persisting form data
const STORAGE_KEY = 'therapist-registration-data';

// Validation schemas for each step
export const basicInfoSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().regex(/^\+[1-9]\d{1,14}$/, 'Phone must be in E.164 format (e.g., +12025551234)'),
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
  confirmPassword: z.string().optional(),
}).refine(
  (data) => {
    if (data.password && data.confirmPassword) {
      return data.password === data.confirmPassword;
    }
    return true;
  },
  {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  }
);

export const professionalInfoSchema = z.object({
  licenseType: z.enum(['LCSW', 'LPC', 'LMFT', 'PhD', 'PsyD', 'Licensed Psychologist'], {
    errorMap: () => ({ message: 'Please select a license type' }),
  }),
  state: z.string().min(2, 'Please select a state'),
  licenseNumber: z.string().min(3, 'License number is required'),
  licenseExpiration: z.string().min(1, 'License expiration date is required'),
  npiNumber: z.string().regex(/^\d{10}$/, 'NPI must be exactly 10 digits').optional().or(z.literal('')),
});

export const documentUploadSchema = z.object({
  governmentId: z.object({
    file: z.any(),
    name: z.string(),
    url: z.string().optional(),
  }).nullable().refine((val) => val !== null, 'Government ID is required'),
  professionalLicense: z.object({
    file: z.any(),
    name: z.string(),
    url: z.string().optional(),
  }).nullable().refine((val) => val !== null, 'Professional license is required'),
  insuranceCertificate: z.object({
    file: z.any(),
    name: z.string(),
    url: z.string().optional(),
  }).nullable().refine((val) => val !== null, 'Insurance certificate is required'),
});

export const practiceDetailsSchema = z.object({
  specialties: z.array(z.string()).min(1, 'Select at least one specialty'),
  treatmentApproaches: z.array(z.string()).min(1, 'Select at least one treatment approach'),
  sessionRate: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid rate').min(1, 'Session rate is required'),
  sessionTypes: z.array(z.enum(['in-person', 'video', 'both'])).min(1, 'Select at least one session type'),
  officeAddress: z.string().optional(),
  bio: z.string().max(500, 'Bio must be 500 characters or less').min(50, 'Bio must be at least 50 characters'),
});

export const reviewSchema = z.object({
  acceptTerms: z.boolean().refine((val) => val === true, 'You must accept the terms of service'),
});

// Combined schema for all steps
export const registrationSchema = z.object({
  basicInfo: basicInfoSchema,
  professionalInfo: professionalInfoSchema,
  documentUpload: documentUploadSchema,
  practiceDetails: practiceDetailsSchema,
  review: reviewSchema,
});

export type RegistrationFormData = z.infer<typeof registrationSchema>;

export type BasicInfoData = z.infer<typeof basicInfoSchema>;
export type ProfessionalInfoData = z.infer<typeof professionalInfoSchema>;
export type DocumentUploadData = z.infer<typeof documentUploadSchema>;
export type PracticeDetailsData = z.infer<typeof practiceDetailsSchema>;
export type ReviewData = z.infer<typeof reviewSchema>;

export interface UseRegistrationWizardReturn {
  currentStep: number;
  totalSteps: number;
  goToStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  formData: RegistrationFormData;
  updateFormData: (step: keyof RegistrationFormData, data: any) => void;
  submitRegistration: () => Promise<void>;
  isSubmitting: boolean;
  clearFormData: () => void;
}

const defaultFormData: RegistrationFormData = {
  basicInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  },
  professionalInfo: {
    licenseType: 'LCSW',
    state: '',
    licenseNumber: '',
    licenseExpiration: '',
    npiNumber: '',
  },
  documentUpload: {
    governmentId: null,
    professionalLicense: null,
    insuranceCertificate: null,
  },
  practiceDetails: {
    specialties: [],
    treatmentApproaches: [],
    sessionRate: '',
    sessionTypes: [],
    officeAddress: '',
    bio: '',
  },
  review: {
    acceptTerms: false,
  },
};

export function useRegistrationWizard(): UseRegistrationWizardReturn {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<RegistrationFormData>(() => {
    // Load from localStorage on mount
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load saved form data:', error);
    }
    return defaultFormData;
  });

  const totalSteps = 5;

  // Persist to localStorage whenever formData changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (error) {
      console.error('Failed to save form data:', error);
    }
  }, [formData]);

  const updateFormData = (step: keyof RegistrationFormData, data: any) => {
    setFormData((prev) => ({
      ...prev,
      [step]: data,
    }));
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step < totalSteps) {
      setCurrentStep(step);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const submitRegistration = async () => {
    setIsSubmitting(true);
    try {
      // Validate all data
      const validated = registrationSchema.parse(formData);

      // TODO: Replace with actual API call
      console.log('Submitting registration:', validated);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Clear form data on successful submission
      clearFormData();

      // Navigate to success page or dashboard
      console.log('Registration successful!');
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearFormData = () => {
    setFormData(defaultFormData);
    localStorage.removeItem(STORAGE_KEY);
    setCurrentStep(0);
  };

  return {
    currentStep,
    totalSteps,
    goToStep,
    nextStep,
    previousStep,
    formData,
    updateFormData,
    submitRegistration,
    isSubmitting,
    clearFormData,
  };
}

export function useStepForm<T extends z.ZodType>(
  schema: T,
  defaultValues: z.infer<T>
): UseFormReturn<z.infer<T>> {
  return useForm<z.infer<T>>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
  });
}
